import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDeleteCrmTaskTypeMutation,
  useEditCrmTaskTypeMutation,
} from '@app/api/CrmTasks/api';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  crmTasksApi,
  useGetCrmTaskTypesQuery,
  useCreateCrmTaskTypeMutation,
  useCreateTaskMutation,
  useSearchCrmTaskUsersQuery,
} from '@app/services/rtkQuery/CrmTasks/api';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import { useAppDispatch } from '@app/store';

import { DATE_PRESETS, type DatePresetId } from '../data/datePresets';
import { addMinutesToTime, computeChipLabel } from '../lib/taskDateTime';

import type { TaskExecutor } from '../types';
import type { Dayjs } from 'dayjs';

export type SelectionPhase = 'idle' | 'selecting';

const DEFAULT_TASK_TYPE_ID = 1;

export const useTaskComposer = ({ clientId }: { clientId: number | null }) => {
  const today = useMemo(() => dayjs().startOf('day'), []);

  const { activeBranchId: ActiveBranchId, userId, user } = useUserPrint();
  const { showNotification } = useNotification();
  const dispatch = useAppDispatch();
  const { data: taskTypes = [] } = useGetCrmTaskTypesQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );
  const [createCrmTaskTypeMutation] = useCreateCrmTaskTypeMutation();
  const [editCrmTaskTypeMutation] = useEditCrmTaskTypeMutation();
  const [deleteCrmTaskTypeMutation] = useDeleteCrmTaskTypeMutation();
  const [createTask, { isLoading: isSubmitting }] = useCreateTaskMutation();

  const { data: usersData = [] } = useSearchCrmTaskUsersQuery(
    { searchTerm: '', limit: 0, ActiveBranchId: ActiveBranchId! },
    { skip: !ActiveBranchId }
  );

  const executors = useMemo<TaskExecutor[]>(
    () =>
      usersData
        .filter((u) => !u.IsDeleted)
        .map((u) => ({
          id: u.UserId,
          name:
            [u.NameLast, u.NameFirst].filter(Boolean).join(' ') ||
            String(u.UserId),
        })),
    [usersData]
  );

  const currentUserExecutor = useMemo<TaskExecutor | null>(() => {
    if (!userId) return null;

    const userFromOptions = usersData.find(
      (item) => item.UserId === userId && !item.IsDeleted
    );

    if (userFromOptions) {
      return {
        id: userFromOptions.UserId,
        name:
          [userFromOptions.NameLast, userFromOptions.NameFirst]
            .filter(Boolean)
            .join(' ') || String(userFromOptions.UserId),
      };
    }

    const fallbackName = [user?.nameLast, user?.nameFirst]
      .filter(Boolean)
      .join(' ');

    return {
      id: userId,
      name: fallbackName || String(userId),
    };
  }, [user?.nameFirst, user?.nameLast, userId, usersData]);

  const [selectedTaskType, setSelectedTaskType] =
    useState<NCrmTasks.ModelCrmTaskType | null>(null);

  useEffect(() => {
    if (selectedTaskType === null && taskTypes.length > 0) {
      const defaultTaskType =
        taskTypes.find(({ Id }) => Id === DEFAULT_TASK_TYPE_ID) ??
        taskTypes[0] ??
        null;

      setSelectedTaskType(defaultTaskType);
    }
  }, [taskTypes, selectedTaskType]);

  const selectTaskType = useCallback((type: NCrmTasks.ModelCrmTaskType) => {
    setSelectedTaskType(type);
  }, []);

  const createTaskType = useCallback(
    async (params: {
      iconId: string;
      color: string;
      name: string;
    }): Promise<NCrmTasks.ModelCrmTaskType | null> => {
      try {
        const created = await createCrmTaskTypeMutation({
          ActiveBranchId,
          data: {
            Id: 0,
            IconId: params.iconId,
            Color: params.color,
            Name: params.name,
          },
        }).unwrap();
        setSelectedTaskType(created);
        return created;
      } catch {
        return null;
      }
    },
    [ActiveBranchId, createCrmTaskTypeMutation]
  );

  const editTaskType = useCallback(
    async (type: NCrmTasks.ModelCrmTaskType): Promise<void> => {
      if (!ActiveBranchId) return;
      try {
        await editCrmTaskTypeMutation({
          ActiveBranchId,
          data: {
            Id: type.Id,
            IconId: type.IconId ?? null,
            Color: type.Color ?? null,
            Name: type.Name,
          },
        }).unwrap();
        if (selectedTaskType?.Id === type.Id) {
          setSelectedTaskType(type);
        }
        // The edit mutation lives in a different RTK Query slice than the
        // task-types list query, so invalidate the list tag explicitly to pull
        // the server-confirmed values back into the dropdown.
        dispatch(crmTasksApi.util.invalidateTags(['CrmTaskTypes']));
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      dispatch,
      editCrmTaskTypeMutation,
      selectedTaskType?.Id,
      showNotification,
    ]
  );

  const deleteTaskType = useCallback(
    async (id: number): Promise<void> => {
      if (!ActiveBranchId) return;
      try {
        await deleteCrmTaskTypeMutation({ ActiveBranchId, id }).unwrap();
        if (selectedTaskType?.Id === id) {
          const remaining = taskTypes.filter((t) => t.Id !== id);
          setSelectedTaskType(remaining[0] ?? null);
        }
        dispatch(crmTasksApi.util.invalidateTags(['CrmTaskTypes']));
        showNotification('Тип задачи удалён', 'success');
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      deleteCrmTaskTypeMutation,
      dispatch,
      selectedTaskType?.Id,
      showNotification,
      taskTypes,
    ]
  );

  const [selectedExecutor, setSelectedExecutor] = useState<TaskExecutor | null>(
    currentUserExecutor
  );
  const hasSelectedExecutorManuallyRef = useRef(false);

  useEffect(() => {
    if (hasSelectedExecutorManuallyRef.current || !currentUserExecutor) return;

    setSelectedExecutor(currentUserExecutor);
  }, [currentUserExecutor]);

  const selectExecutor = useCallback((executor: TaskExecutor) => {
    hasSelectedExecutorManuallyRef.current = true;
    setSelectedExecutor(executor);
  }, []);

  // ── Date state ────────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState<Dayjs>(today);
  const [endDate, setEndDate] = useState<Dayjs>(today);
  const [preset, setPreset] = useState<DatePresetId | null>('today');
  const [selectionPhase, setSelectionPhase] = useState<SelectionPhase>('idle');
  const [pendingStart, setPendingStart] = useState<Dayjs | null>(null);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    dayjs().startOf('month')
  );

  // ── Time state ────────────────────────────────────────────────────────────
  const [isAllDay, setIsAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');

  // ── Chip label ────────────────────────────────────────────────────────────
  const chipLabel = useMemo(
    () =>
      computeChipLabel(
        startDate,
        endDate,
        preset,
        isAllDay,
        startTime,
        endTime
      ),
    [startDate, endDate, preset, isAllDay, startTime, endTime]
  );

  const previewStart = useMemo((): Dayjs | null => {
    if (selectionPhase !== 'selecting' || !pendingStart) return null;
    if (!hoverDate) return pendingStart;
    return hoverDate.isBefore(pendingStart, 'day') ? hoverDate : pendingStart;
  }, [selectionPhase, pendingStart, hoverDate]);

  const previewEnd = useMemo((): Dayjs | null => {
    if (selectionPhase !== 'selecting' || !pendingStart) return null;
    if (!hoverDate) return pendingStart;
    return hoverDate.isAfter(pendingStart, 'day') ? hoverDate : pendingStart;
  }, [selectionPhase, pendingStart, hoverDate]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const selectPreset = useCallback((presetId: DatePresetId) => {
    const found = DATE_PRESETS.find((p) => p.id === presetId);
    if (!found) return;
    const date = found.getDate();
    setStartDate(date);
    setEndDate(date);
    setPreset(presetId);
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
    setViewMonth(date.startOf('month'));
    setIsAllDay(found.isAllDay);
    if (!found.isAllDay) {
      const st = found.getStartTime();
      const et = found.getEndTime();
      if (st) setStartTime(st);
      if (et) setEndTime(et);
    }
  }, []);

  const handleDayClick = useCallback(
    (day: Dayjs) => {
      if (selectionPhase === 'idle') {
        // A single click already fixes the period to that day; a second click
        // extends it to a range.
        setStartDate(day);
        setEndDate(day);
        setPendingStart(day);
        setHoverDate(day);
        setSelectionPhase('selecting');
        setPreset(null);
      } else if (pendingStart) {
        if (
          day.isSame(pendingStart, 'day') ||
          day.isAfter(pendingStart, 'day')
        ) {
          setStartDate(pendingStart);
          setEndDate(day);
        } else {
          setStartDate(day);
          setEndDate(pendingStart);
        }
        setPendingStart(null);
        setSelectionPhase('idle');
        setHoverDate(null);
      }
    },
    [selectionPhase, pendingStart]
  );

  // Called when the date popup closes: an unfinished range stays a single day.
  const finishDateSelection = useCallback(() => {
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
  }, []);

  const handleDayHover = useCallback(
    (day: Dayjs | null) => {
      if (selectionPhase === 'selecting') setHoverDate(day);
    },
    [selectionPhase]
  );

  const toggleAllDay = useCallback(() => {
    setIsAllDay((prev) => {
      if (prev) {
        // switching to timed — chip should show date+time, not just preset name
        setPreset(null);
      }
      return !prev;
    });
  }, []);

  const handleStartTimeChange = useCallback((time: string) => {
    if (!time) return;
    setStartTime(time);
    setPreset(null);
    // Auto-advance end time so it stays after start
    const [sh, sm] = time.split(':').map(Number);
    const startTotal = sh * 60 + sm;
    setEndTime((prev) => {
      const [eh, em] = prev.split(':').map(Number);
      if (eh * 60 + em > startTotal) return prev;
      return addMinutesToTime(time, 60);
    });
  }, []);

  const handleEndTimeChange = useCallback((time: string) => {
    if (!time) return;
    setEndTime(time);
    setPreset(null);
  }, []);

  // Sets endTime = startTime + N minutes (fixed duration from start)
  const handleSetDuration = useCallback(
    (minutes: number) => {
      setEndTime(addMinutesToTime(startTime, minutes));
      setPreset(null);
    },
    [startTime]
  );

  const handleSubmit = useCallback(
    async (description: string): Promise<boolean> => {
      if (!selectedTaskType) {
        showNotification('Выберите тип задачи', 'frontWarning');
        return false;
      }
      if (!ActiveBranchId) {
        return false;
      }

      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const timeStart = isAllDay
        ? `${startDateStr}T00:00:00`
        : `${startDateStr}T${startTime}:00`;
      const timeDeadline = isAllDay
        ? `${endDateStr}T23:59:59`
        : `${endDateStr}T${endTime}:00`;
      const selectedExecutorId =
        selectedExecutor === null ? null : Number(selectedExecutor.id);
      const ownerIds =
        selectedExecutorId !== null && Number.isFinite(selectedExecutorId)
          ? [selectedExecutorId]
          : null;

      try {
        await createTask({
          activeBranchId: ActiveBranchId,
          data: {
            TaskTypeId: selectedTaskType.Id,
            Description: description.trim() || null,
            TimeStart: timeStart,
            TimeDeadline: timeDeadline,
            IsDeadlineAllDay: isAllDay,
            Color: selectedTaskType.Color,
            ClientId: clientId,
            OwnerIds: ownerIds,
          },
        }).unwrap();
        showNotification('Задача успешно создана', 'success');
        return true;
      } catch (error) {
        showNotification(error);
        return false;
      }
    },
    [
      ActiveBranchId,
      clientId,
      createTask,
      endDate,
      endTime,
      isAllDay,
      selectedExecutor,
      selectedTaskType,
      showNotification,
      startDate,
      startTime,
    ]
  );

  return {
    chipLabel,
    startDate,
    endDate,
    preset,
    selectionPhase,
    pendingStart,
    previewStart,
    previewEnd,
    viewMonth,
    selectPreset,
    handleDayClick,
    handleDayHover,
    finishDateSelection,
    setViewMonth,
    isAllDay,
    startTime,
    endTime,
    toggleAllDay,
    handleStartTimeChange,
    handleEndTimeChange,
    handleSetDuration,
    selectedExecutor,
    responsibleLabel: selectedExecutor?.name ?? 'Не выбран',
    isResponsibleMuted: selectedExecutor === null,
    selectExecutor,
    taskTypes,
    selectedTaskType,
    selectTaskType,
    createTaskType,
    editTaskType,
    deleteTaskType,
    taskTypeLabel: selectedTaskType?.Name ?? 'Тип задачи',
    taskTypeIconId: selectedTaskType?.IconId ?? null,
    taskTypeColor: selectedTaskType?.Color ?? null,
    isTaskTypeMuted: selectedTaskType === null,
    executors,
    isSubmitting,
    handleSubmit,
  };
};
